// src/core/damage.ts

import type {
  BoostStage,
  DamageInput,
  DamageResult,
  Skill,
  StatBlock,
} from './types';
import { getTypeEffectiveness } from '../data/type-chart';

// ============================================================
// ⚙️ 可调参数
// ============================================================
const CONFIG = {
  /** 随机浮动：0.85 ~ 1.00 共 16 段 */
  randomRolls: Array.from({ length: 16 }, (_, i) => 0.85 + i * 0.01),
  /** 本系加成：图片明确 1.25 */
  stabMultiplier: 1.25,
  /** 暴击倍率 */
  critMultiplier: 1.5,
  /** 天气加成/削弱 */
  weatherBoost: 1.5,
  weatherWeaken: 0.5,
};

// 天气影响属性
const WEATHER_BOOST: Record<string, string[]> = {
  sun: ['火'],
  rain: ['水'],
  sand: ['地'],
  snow: ['冰'],
};
const WEATHER_WEAKEN: Record<string, string[]> = {
  sun: ['水'],
  rain: ['火'],
};

// ============================================================
// 辅助函数
// ============================================================

function stageMultiplier(stage: number): number {
  const s = Math.max(-20, Math.min(20, stage));
  // 洛克王国：每层 +10%，即 +1层=1.1倍，-1层=0.9倍
  return s >= 0 ? 1 + s * 0.1 : 1 - Math.abs(s) * 0.1;
}

function applyBoost(base: number, stage = 0): number {
  // 与面板显示口径一致：每级 ±10%，四舍五入
  return Math.round(base * stageMultiplier(stage));
}

function getAttackStat(
  stats: StatBlock,
  skill: Skill,
  boosts?: BoostStage,
): number {
  if (skill.category === 'physical') {
    return applyBoost(stats.atk, boosts?.atk ?? 0);
  }
  if (skill.category === 'special') {
    return applyBoost(stats.spa, boosts?.spa ?? 0);
  }
  return 0;
}

function getDefenseStat(
  stats: StatBlock,
  skill: Skill,
  boosts?: BoostStage,
): number {
  if (skill.category === 'physical') {
    return applyBoost(stats.def, boosts?.def ?? 0);
  }
  if (skill.category === 'special') {
    return applyBoost(stats.spd, boosts?.spd ?? 0);
  }
  return 1;
}

function getWeatherModifier(type: string, weather?: string): number {
  if (!weather || weather === 'none') return 1;
  if (WEATHER_BOOST[weather]?.includes(type)) return CONFIG.weatherBoost;
  if (WEATHER_WEAKEN[weather]?.includes(type)) return CONFIG.weatherWeaken;
  return 1;
}

// 统一去掉“系”字并去空格，兼容 '草' 和 '草系' 两种数据格式
function normalizeType(t: string): string {
  return t.replace('系', '').trim();
}

// ============================================================
// 主计算入口
// ============================================================

export function calculateDamage(input: DamageInput): DamageResult {
  const { attacker, defender, field } = input;
  const { skill } = attacker;

  const hp = Math.max(1, defender.stats.hp);

  // 变化技能或威力为 0
  if (skill.category === 'status' || skill.power <= 0) {
    return {
      min: 0, max: 0, avg: 0,
      percentMin: 0, percentMax: 0,
      remainingHp: hp, koChance: 0, rolls: [],
    };
  }

  // 1. 获取攻防面板
  const atk = getAttackStat(attacker.stats, skill, attacker.boosts);
  const def = Math.max(1, getDefenseStat(defender.stats, skill, defender.boosts));

  // 2. 属性归一化
  const skillType = normalizeType(skill.type);
  const attackerTypes = attacker.spirit.types.map(normalizeType);
  const defenderTypes = defender.spirit.types.map(normalizeType);

  // 3. 计算"应对倍率"（本系 × 克制）
  const stab = attackerTypes.includes(skillType) ? CONFIG.stabMultiplier : 1;
  const typeEff = getTypeEffectiveness(skillType, defenderTypes);

  // 属性无效判定
  if (typeEff === 0) {
    return {
      min: 0, max: 0, avg: 0,
      percentMin: 0, percentMax: 0,
      remainingHp: hp, koChance: 0, rolls: [],
    };
  }
  
  const synergyMultiplier = stab * typeEff;

  // 4. 计算"最终威力"（技能威力 × 应对倍率 + 威力加成）
  // 目前暂无"威力加成"数据，先设为0
  const powerBonus = 0; 
  const finalPower = skill.power * synergyMultiplier + powerBonus;

  // 5. 计算基础伤害（严格按照图片公式：攻/防 × 0.9 × 最终威力）
  const base = Math.round((atk / def) * (37/41) * finalPower);

  

  // 6. 额外乘数（暴击、天气）
  const crit = field?.crit ? CONFIG.critMultiplier : 1;
  const weather = getWeatherModifier(skillType, field?.weather);
  const globalMultiplier = crit * weather;

  // 7. 随机浮动（0.85 ~ 1.00）
  const rolls = CONFIG.randomRolls.map((r) =>
    Math.max(1, Math.floor(base * globalMultiplier * r)),
  );

  const min = Math.min(...rolls);
  const max = Math.max(...rolls);
  const avg = Math.round(rolls.reduce((a, b) => a + b, 0) / rolls.length);

  return {
    min,
    max,
    avg,
    percentMin: (min / hp) * 100,
    percentMax: (max / hp) * 100,
    remainingHp: Math.max(0, hp - max),
    koChance: rolls.filter((d) => d >= hp).length / rolls.length,
    rolls,
  };
}